/** @format */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PaperedTable from '../../../components/Table/PaperedTable';
import { TableBody, TableCell, TableFooter, TableHead, TableRow } from '@material-ui/core';
import TableHeaderContentCell from '../../../components/Table/TableHeaderContentCell';
import Filter from '../../../interfaces/forms/Filter';
import Order from '../../../interfaces/forms/Order';
import { useDispatch, useSelector } from 'react-redux';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import CustomTextButtom from '../../../components/Buttons/CustomTextButton';
import FilterList from '../../../components/Filter/FilterList';
import Pager from '../../../components/Adescom/PagerRange';
import { FilterReducerFieldName, FilterReducers } from '../../../modules/filter/FilterReducers';
import { FilterListReducerFieldName } from '../../../modules/filter/FilterListReducers';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import InvoiceAddDialog from './InvoiceAddDialog';
import FabAddButton from '../../../components/Buttons/FabAddButton';
import {
    HeaderCheckBoxSelectListElement,
    HeaderCheckBoxSelectListElementValueString,
} from '../../lib/search/listTableHeader/HeaderCheckBoxSelectList';
import { LoadPlatformResellersType, PlatformResellerLoader } from '../../../modules/platformreseller/PlatformResellerLoader';
import InvoiceItem from './InvoiceItem';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { InvoiceCollectionInterface, InvoiceInterface } from '../../../interfaces/platform/InvoiceInterface';
import { documentConstants } from '../../../modules/document/actions';
import { DocumentInterface } from '../../../interfaces/platform/DocumentInterface';
import { DownloadFileFromResponse } from '../../../helpers/DownloadHelper';
import CostReportModal from './CostReportModal';
import { FilterManager } from '../../../modules/filter/FilterManager';
import { PaymentDeadlineCodeCollectionInterface } from '../../../interfaces/collections/PaymentDeadlineCodeCollectionInterface';
import { PaymentDeadlineCode } from '../../../interfaces/platform/PaymentDeadlineCode';
import { PaymentDeadlineCodeLoader } from '../../../modules/paymentDeadlineCode/paymentDeadlineCodeLoader';
import { InvoiceToCsv } from '../../../modules/document/InvoiceToCsv';
import { PreprocessFilters } from '../../../helpers/FilterHelper';
import { useSnackbar } from 'notistack';
import withStyles from '@material-ui/core/styles/withStyles';
import oplAppStyle from '../../../assets/themes/OPL/jss/App/OplAppStyle';
import { AlertDialog } from '../../lib/dialog/AlertDialog';
import { InvoicePaidDialog } from './InvoicePaidDialog';
import { VatCollectionInterface } from '../../../interfaces/collections/VatCollectionInterface';
import { useVats } from '../../../modules/document/useVats';

type InvoiceListType = {
    classes: any;
    showMainLoader(show: boolean): void;
};

const setDefaultFilters = (): Array<Filter> => [];
const setDefaultOrders = (): Array<Order> => [{ name: 'soldOn', value: 'DESC' }];

const InvoiceList = (props: InvoiceListType): React.ReactElement => {
    const classes = props.classes;
    const { showMainLoader } = props;
    const dispatch = useDispatch();
    const { headerFilterElementHasValue, headerFilterElementName } = useSelector((state: { filterReducer: FilterReducers }) => state.filterReducer);
    const [showClearFilterButton, setShowClearFilterButton] = useState(false);
    const [reload, setReload] = useState<boolean>(false);
    const [showAdd, setShowAdd] = useState<boolean>(false);
    const [resellers, setResellers] = useState<Array<HeaderCheckBoxSelectListElement>>([]);
    const [invoices, setInvoices] = useState<InvoiceCollectionInterface | null>();
    const [selectedReportInvoice, setSelectedReportInvoice] = useState<InvoiceInterface | null>(null);
    const [paymentCodes, setPaymentCodes] = useState<PaymentDeadlineCodeCollectionInterface | null>(null);
    const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(null);
    const [openSetAsPaidDialog, setOpenSetAsPaidDialog] = useState<boolean>(false);
    const [paidId, setPaidId] = useState<number | null>(null);
    const { enqueueSnackbar } = useSnackbar();
    const [invoiceTypeList, setInvoiceTypeList] = useState<Array<HeaderCheckBoxSelectListElementValueString>>([
        { name: 'documents:invoices.type.1', value: '1' },
        { name: 'documents:invoices.type.2', value: '2' },
        { name: 'documents:invoices.type.3', value: '3' },
        { name: 'documents:invoices.type.4', value: '4' },
    ]);

    const { vats, vatsSelect, triedLoading } = useVats();

    const [ksefStatusList, setKsefStatusList] = useState<Array<HeaderCheckBoxSelectListElementValueString>>([
        { name: 'KSEF_COMPLETED', value: 'KSEF_COMPLETED' },
        { name: 'KSEF_EMERGENCY_MODE', value: 'KSEF_EMERGENCY_MODE' },
        { name: 'KSEF_TOTAL_FAILURE', value: 'KSEF_TOTAL_FAILURE' },
        { name: 'REJECTED', value: 'REJECTED' },
        { name: 'TRANSITION', value: 'TRANSITION' },
    ]);
    const [ksefPackageStatusList, setPackageKsefStatusList] = useState<Array<HeaderCheckBoxSelectListElementValueString>>([
        { name: 'OPL2KSEF_NEW', value: 'OPL2KSEF_NEW' },
        { name: 'OPL2KSEF_PROCESSING', value: 'OPL2KSEF_PROCESSING' },
        { name: 'OPL2KSEF_READY_TO_SEND', value: 'OPL2KSEF_READY_TO_SEND' },
        { name: 'KSEF_SENT', value: 'KSEF_SENT' },
        { name: 'OPL2KSEF_TRANSITION', value: 'OPL2KSEF_TRANSITION' },
        { name: 'KSEF_COMPLETED', value: 'KSEF_COMPLETED' },
        { name: 'KSEF_ERROR', value: 'KSEF_ERROR' },
        { name: 'KSEF_EMERGENCY_MODE', value: 'KSEF_EMERGENCY_MODE' },
        { name: 'KSEF_TOTAL_FAILURE', value: 'KSEF_TOTAL_FAILURE' },
    ]);
    const [paymentCodesPrepared, setPaymentCodesPrepared] = useState<Array<HeaderCheckBoxSelectListElementValueString> | null>(null);

    const itemLoader: ItemLoaders = useMemo(() => {
        return new ItemLoaders(dispatch);
    }, [dispatch]);
    const platformResellersLoader: PlatformResellerLoader = useMemo(() => {
        return new PlatformResellerLoader(itemLoader);
    }, [itemLoader]);

    const tableStickyHeader = { root: classes.tableStickyHeader };
    const tableStickyCommand = { root: classes.tableStickyCommand };
    const tableControlStickyHeader = { root: classes.tableControlStickyHeader };

    const [filters, setFilters] = useState<Array<Filter>>(setDefaultFilters());
    const [orders, setOrders] = useState<Array<Order>>(setDefaultOrders());

    const paymentDeadlineCodeLoader = useMemo(() => {
        return new PaymentDeadlineCodeLoader(itemLoader);
    }, [itemLoader]);

    const testCodes = useCallback((): void => {
        setPaymentCodes(paymentDeadlineCodeLoader.getPaymentDeadLineCodes());
    }, [paymentDeadlineCodeLoader]);

    const firstLoad = useCallback((): void => {
        showMainLoader(true);

        itemLoader.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/invoices')),
            documentConstants.INVOICES_LIST,
            PreprocessFilters(filters),
            orders,
            (items: InvoiceCollectionInterface): void => {
                setInvoices(null);
                setInvoices(items);
                showMainLoader(false);
                dispatch({
                    type: FilterListReducerFieldName.HEADER_FILTER_MANAGE,
                    showRender: true,
                });
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );

        paymentDeadlineCodeLoader.loadPaymentDeadlineCodes([], [], testCodes);
    }, [paymentDeadlineCodeLoader, testCodes, showMainLoader, itemLoader, dispatch, filters, orders, setInvoices]);

    useEffect(() => {
        if (reload) {
            setReload(false);
            firstLoad();
        }
    }, [reload, showMainLoader, firstLoad]);

    useEffect(() => {
        setReload(true);
    }, []);

    useEffect(() => {
        if (paymentCodes) {
            setPaymentCodesPrepared(
                paymentCodes['hydra:member'].map((el: PaymentDeadlineCode) => {
                    return { name: `${el.code} - ${el.mainname}`, value: `${el.code}` };
                })
            );
        }
    }, [paymentCodes]);

    const clearFilter = (): void => {
        setFilters(setDefaultFilters());
        setOrders(setDefaultOrders());
        setShowClearFilterButton(false);
        dispatch({
            type: FilterReducerFieldName.HEADER_FILTER_MANAGE,
            clearAll: true,
        });
        dispatch({
            type: FilterListReducerFieldName.HEADER_FILTER_MANAGE,
        });
        setReload(true);
    };

    useEffect(() => {
        const filterManage = new FilterManager();
        if (headerFilterElementName) {
            if (headerFilterElementHasValue) {
                filterManage.manageElement(headerFilterElementName, headerFilterElementHasValue);
            } else {
                filterManage.manageElement(headerFilterElementName, false);
            }
            setShowClearFilterButton(filterManage.hasManageElement());
        }
    }, [headerFilterElementName, headerFilterElementHasValue, dispatch]);

    const paginationCallbackFunction = useCallback(
        (uri: string): void => {
            showMainLoader(true);
            const itemLoader = new ItemLoaders(dispatch);

            itemLoader.paginationCallWithCallback(
                uri,
                documentConstants.INVOICES_LIST,
                (items: InvoiceCollectionInterface): void => {
                    setInvoices(null);
                    setInvoices(items);
                    showMainLoader(false);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        },
        [showMainLoader, setInvoices, dispatch]
    );

    const resellersCallback = useCallback((): void => {
        setResellers(platformResellersLoader.getPlatformResellersHeaderList());
    }, [setResellers, platformResellersLoader]);

    useEffect((): void => {
        const params: LoadPlatformResellersType = {
            callback: resellersCallback,
            filters: [],
        };
        platformResellersLoader.loadPlatformResellers(params);
    }, [platformResellersLoader, resellersCallback]);

    const acceptDialogToggleCallback = (invoiceId: number): void => setSelectedInvoiceId(invoiceId);

    const acceptCallback = useCallback(
        (id: number): void => {
            const itemLoader = new ItemLoaders(dispatch);

            itemLoader.postWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/custom_document_actions')),
                documentConstants.INVOICE_PUT,
                { action: 'invoice.accept', params: JSON.stringify({ id: id }) },
                firstLoad,
                {
                    propagateDataToCallback: false,
                    dispatchAfterCallback: false,
                    dispatchErrorAfterCallback: false,
                }
            );
        },
        [dispatch, firstLoad]
    );

    const openSetAsPaid = (id: number): void => {
        setOpenSetAsPaidDialog(true);
        setPaidId(id);
    };

    const closeSetAsPaid = (): void => {
        setOpenSetAsPaidDialog(false);
        setPaidId(null);
    };

    const setAsPaidCallback = useCallback(
        (id: number, date: string): void => {
            const itemLoader = new ItemLoaders(dispatch);

            itemLoader.postWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/custom_document_actions')),
                documentConstants.INVOICE_PUT,
                { action: 'invoice.update_paid', params: JSON.stringify({ id: id, date: date }) },
                () => {
                    setPaidId(null);
                    firstLoad();
                },
                {
                    propagateDataToCallback: false,
                    dispatchAfterCallback: false,
                    dispatchErrorAfterCallback: false,
                }
            );
        },
        [dispatch, firstLoad]
    );

    const resendPivotCallback = useCallback(
        (id: number): void => {
            const itemLoader = new ItemLoaders(dispatch);

            itemLoader.postWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/custom_document_actions')),
                documentConstants.INVOICE_PUT,
                { action: 'invoice.resend_pivot', params: JSON.stringify({ id: id }) },
                () => {
                    // enqueueSnackbar(i18n.t('documents:invoices.pivot_resend_request_success'), { variant: 'success', autoHideDuration: 4000 });
                    firstLoad();
                },
                {
                    propagateDataToCallback: false,
                    dispatchAfterCallback: false,
                    dispatchErrorAfterCallback: false,
                }
            );
        },
        [dispatch, firstLoad /**, enqueueSnackbar**/]
    );

    const cancelClauseCallback = useCallback(
        (id: number): void => {
            const itemLoader = new ItemLoaders(dispatch);

            itemLoader.postWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/custom_document_actions')),
                documentConstants.INVOICE_PUT,
                { action: 'invoice.cancel', params: JSON.stringify({ id: id }) },
                firstLoad,
                {
                    propagateDataToCallback: false,
                    dispatchAfterCallback: false,
                    dispatchErrorAfterCallback: false,
                }
            );
        },
        [dispatch, firstLoad]
    );

    const requestFileDownload = useCallback(
        (document: DocumentInterface, type = 'document_set'): void => {
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getResponseWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath(`/download_documents/${type}/${document.downloadHash}`)),
                documentConstants.FILE_DOWNLOAD,
                (response: any) => {
                    DownloadFileFromResponse(response, document);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false, dispatchErrorAfterCallback: false }
            );
        },
        [dispatch]
    );

    const saveCallback = useCallback(
        (params: any): void => {
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.postWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath(`/request_documents`)),
                documentConstants.REQUEST_DOCUMENT,
                params,
                (): void => {
                    setShowAdd(false);
                    setTimeout(() => {
                        firstLoad();
                    }, 1000);
                },
                { propagateDataToCallback: false, dispatchAfterCallback: false, dispatchErrorAfterCallback: false }
            );
        },
        [dispatch, firstLoad]
    );

    const exportToCsv = (): void => {
        const toCsv = new InvoiceToCsv(itemLoader);
        toCsv.exportToCsv(orders, filters, showMainLoader);
    };

    const updateOrderList = (orders: Array<Order>): void => setOrders(orders);
    const updateFilterList = (filters: Array<Filter>): void => setFilters(filters);

    return (
        <>
            <GridContainer>
                <GridItem xs={12} md={12} lg={12}>
                    <PaperedTable
                        beforeTableContent={
                            <GridContainer>
                                <GridItem xs={12} className={classes.beforeTableContent}>
                                    <CustomTextButtom
                                        callback={(): void => {
                                            props.showMainLoader(true);
                                            setReload(true);
                                        }}
                                        callbackParam={{}}
                                        color="primary"
                                        label="general:common.search"
                                        size="lg"
                                    />
                                    {showClearFilterButton && (
                                        <CustomTextButtom
                                            callback={clearFilter}
                                            callbackParam={{}}
                                            color="primary"
                                            label="general:common.clear"
                                            size="lg"
                                        />
                                    )}
                                    <CustomTextButtom
                                        callback={exportToCsv}
                                        callbackParam={{}}
                                        color="primary"
                                        label="general:common.export_to_csv"
                                        size="lg"
                                    />
                                    <FilterList />
                                </GridItem>
                            </GridContainer>
                        }
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell classes={tableStickyCommand} padding="none" align="left">
                                    <TableHeaderContentCell textAlign="left" title="general:common.commands" onlyFilter={true} />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="documents:invoices.invoice_type"
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        fieldName="type"
                                        showSort={true}
                                        searchFieldType="select"
                                        selectComponent="headerCheckBoxSimpleList"
                                        elementList={invoiceTypeList}
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} padding="none" align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        fieldName="soldOn"
                                        title="documents:invoices.sold_on"
                                        showSort={true}
                                        onlyFilter={false}
                                        defaultDirection="asc"
                                        searchFieldType="date"
                                        dateFormat="RRRR-MM-DD"
                                        appendAutomaticTimeToBeforeAfterDate={true}
                                        dateFromTo={true}
                                        filters={filters}
                                        sort={orders}
                                        breakLine={true}
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        searchActionCallback={firstLoad}
                                        nullable={false}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} padding="none" align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="documents:invoices.sold_for"
                                        fieldName="soldFor"
                                        showSort={true}
                                        onlyFilter={false}
                                        defaultDirection="asc"
                                        searchFieldType="date"
                                        dateFormat="RRRR-MM-DD"
                                        appendAutomaticTimeToBeforeAfterDate={true}
                                        dateFromTo={true}
                                        filters={filters}
                                        sort={orders}
                                        breakLine={true}
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        searchActionCallback={firstLoad}
                                        nullable={false}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="documents:invoices.invoice_number"
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        fieldName="invoiceNumber"
                                        showSort={true}
                                        onlyFilter={false}
                                        defaultDirection="desc"
                                        searchFieldType="string"
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="documents:invoices.seller"
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        fieldName="seller.id"
                                        showSort={true}
                                        elementList={resellers}
                                        searchFieldType="select"
                                        selectComponent="headerCheckBoxSimpleList"
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="documents:invoices.buyer"
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        fieldName="buyer.id"
                                        showSort={true}
                                        elementList={resellers}
                                        searchFieldType="select"
                                        selectComponent="headerCheckBoxSimpleList"
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="documents:invoices.agreement"
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        fieldName="agreement"
                                        showSort={true}
                                        onlyFilter={false}
                                        defaultDirection="desc"
                                        searchFieldType="string"
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} padding="none" align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="documents:invoices.payment_deadline"
                                        fieldName="paymentDeadline"
                                        showSort={true}
                                        onlyFilter={false}
                                        defaultDirection="asc"
                                        searchFieldType="date"
                                        dateFormat="RRRR-MM-DD"
                                        appendAutomaticTimeToBeforeAfterDate={true}
                                        dateFromTo={true}
                                        filters={filters}
                                        sort={orders}
                                        breakLine={true}
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        searchActionCallback={firstLoad}
                                        nullable={false}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="documents:invoices.payment_deadline_clause"
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        fieldName="paymentDeadlineClause"
                                        elementList={paymentCodesPrepared}
                                        showSort={true}
                                        searchFieldType="select"
                                        selectComponent="headerCheckBoxSimpleList"
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="documents:invoices.amount"
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        fieldName="amount"
                                        showSort={true}
                                        onlyFilter={false}
                                        defaultDirection="desc"
                                        searchFieldType="string"
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="documents:invoices.tax_percentage"
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        fieldName="vat.id"
                                        showSort={true}
                                        onlyFilter={false}
                                        defaultDirection="desc"
                                        searchFieldType="select"
                                        selectComponent="headerCheckBoxSimpleList"
                                        elementList={vatsSelect ?? []}
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="documents:invoices.tax_amount"
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        fieldName="taxAmount"
                                        showSort={true}
                                        onlyFilter={false}
                                        defaultDirection="desc"
                                        searchFieldType="string"
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="documents:invoices.no_tax_amount"
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        fieldName="noTaxAmount"
                                        showSort={true}
                                        onlyFilter={false}
                                        defaultDirection="desc"
                                        searchFieldType="string"
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="documents:invoices.paid_on"
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        fieldName="paidOn"
                                        showSort={true}
                                        searchFieldType="date"
                                        dateFormat="RRRR-MM-DD"
                                        appendAutomaticTimeToBeforeAfterDate={true}
                                        dateFromTo={true}
                                        filters={filters}
                                        sort={orders}
                                        breakLine={true}
                                        selectComponent="headerBooleanCheckBoxSelectList"
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="documents:invoices.accepted"
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        fieldName="accepted"
                                        showSort={true}
                                        searchFieldType="select"
                                        selectComponent="headerBooleanCheckBoxSelectList"
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="documents:invoices.accepted_on"
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        fieldName="acceptedOn"
                                        showSort={true}
                                        searchFieldType="date"
                                        dateFormat="RRRR-MM-DD"
                                        appendAutomaticTimeToBeforeAfterDate={true}
                                        dateFromTo={true}
                                        filters={filters}
                                        sort={orders}
                                        breakLine={true}
                                        selectComponent="headerBooleanCheckBoxSelectList"
                                    />
                                </TableCell>
                                {/*<TableCell classes={tableStickyHeader} align="center">*/}
                                {/*    <TableHeaderContentCell*/}
                                {/*        textAlign="center"*/}
                                {/*        title="documents:invoices.deleted"*/}
                                {/*        updateOrderList={updateOrderList}*/}
                                {/*        updateFilterList={updateFilterList}*/}
                                {/*        fieldName="deleted"*/}
                                {/*        showSort={true}*/}
                                {/*        searchFieldType="select"*/}
                                {/*        selectComponent="headerBooleanCheckBoxSelectList"*/}
                                {/*        filters={filters}*/}
                                {/*        sort={orders}*/}
                                {/*    />*/}
                                {/*</TableCell>*/}
                                <TableCell classes={tableStickyHeader} align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="documents:invoices.pivot"
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        fieldName="hasPivot"
                                        showSort={true}
                                        searchFieldType="select"
                                        selectComponent="headerBooleanCheckBoxSelectList"
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="documents:invoices.iaf"
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        fieldName="hasIaf"
                                        showSort={true}
                                        searchFieldType="select"
                                        selectComponent="headerBooleanCheckBoxSelectList"
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} padding="none" align="left">
                                    <TableHeaderContentCell
                                        autoComplete={'off'}
                                        textAlign="left"
                                        title="documents:invoices.created_at"
                                        fieldName="createdAt"
                                        showSort={true}
                                        onlyFilter={false}
                                        defaultDirection="asc"
                                        searchFieldType="date"
                                        dateFormat="RRRR-MM-DD"
                                        dateFromTo={true}
                                        filters={filters}
                                        sort={orders}
                                        breakLine={true}
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        searchActionCallback={firstLoad}
                                        nullable={false}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} padding="none" align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="documents:invoices.ksefStatus"
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        fieldName="ksefStatus"
                                        showSort={true}
                                        searchFieldType="select"
                                        selectComponent="headerCheckBoxSimpleList"
                                        elementList={ksefStatusList}
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} padding="none" align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="documents:invoices.ksefPackageStatus"
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        fieldName="ksefPackageStatus"
                                        showSort={true}
                                        searchFieldType="select"
                                        selectComponent="headerCheckBoxSimpleList"
                                        elementList={ksefPackageStatusList}
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="documents:invoices.ksefLastUpdate"
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        fieldName="ksefLastUpdate"
                                        showSort={true}
                                        searchFieldType="date"
                                        dateFormat="RRRR-MM-DD"
                                        appendAutomaticTimeToBeforeAfterDate={true}
                                        dateFromTo={true}
                                        filters={filters}
                                        sort={orders}
                                        breakLine={true}
                                        selectComponent="headerBooleanCheckBoxSelectList"
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} padding="none" align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="documents:invoices.ksefPackageId"
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        fieldName="ksefPackageId"
                                        showSort={true}
                                        onlyFilter={false}
                                        defaultDirection="desc"
                                        searchFieldType="string"
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} padding="none" align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="documents:invoices.ksefInvoiceNumber"
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        fieldName="ksefInvoiceNumber"
                                        showSort={true}
                                        onlyFilter={false}
                                        defaultDirection="desc"
                                        searchFieldType="string"
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} padding="none" align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="documents:invoices.ksefError"
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        fieldName="ksefError"
                                        showSort={true}
                                        onlyFilter={false}
                                        defaultDirection="desc"
                                        searchFieldType="string"
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {invoices &&
                                invoices['hydra:member'] &&
                                invoices['hydra:member'].map(
                                    (invoice: InvoiceInterface): React.ReactElement => {
                                        return (
                                            <InvoiceItem
                                                classes={classes}
                                                key={'invoice_item_' + invoice.id}
                                                invoice={invoice}
                                                acceptCallback={(): void => acceptDialogToggleCallback(invoice.id)}
                                                openSetAsPaid={openSetAsPaid}
                                                closeSetAsPaid={closeSetAsPaid}
                                                resendPivotCallback={resendPivotCallback}
                                                cancelClauseCallback={cancelClauseCallback}
                                                downloadFileCallback={requestFileDownload}
                                                setSelectedReportInvoice={setSelectedReportInvoice}
                                                vats={vats}
                                            />
                                        );
                                    }
                                )}
                            {/*<InvoiceItem fakeAccepted={true} />*/}
                            {/*<InvoiceItem />*/}
                        </TableBody>
                        <TableFooter>
                            <TableRow>
                                <TableCell colSpan={18}>
                                    {invoices && invoices['hydra:view'] && <Pager callback={paginationCallbackFunction} views={invoices} />}
                                </TableCell>
                            </TableRow>
                        </TableFooter>
                    </PaperedTable>
                </GridItem>
            </GridContainer>
            <FabAddButton
                callback={(): void => {
                    setShowAdd(true);
                }}
            />
            <InvoiceAddDialog
                openView={showAdd}
                fullWidth={true}
                showMainLoader={showMainLoader}
                closeCallback={(): void => {
                    setShowAdd(false);
                }}
                saveCallback={saveCallback}
                firstLoad={firstLoad}
            />
            <CostReportModal
                fullWidth={true}
                showMainLoader={showMainLoader}
                openView={selectedReportInvoice !== null}
                selectedInvoice={selectedReportInvoice}
                closeCallback={(): void => {
                    setSelectedReportInvoice(null);
                }}
            />
            <AlertDialog
                open={!!selectedInvoiceId}
                titleDialog="documents:invoices.confirm_invoice_accept_title"
                displayMessage="documents:invoices.confirm_invoice_accept"
                closeCallback={(): void => {
                    setSelectedInvoiceId(null);
                }}
                confirmCallback={
                    selectedInvoiceId
                        ? (): void => {
                              acceptCallback(selectedInvoiceId);
                              setSelectedInvoiceId(null);
                          }
                        : (): void => {}
                }
            />
            <InvoicePaidDialog
                openView={openSetAsPaidDialog}
                fullWidth={true}
                showMainLoader={showMainLoader}
                title="documents:invoices.set_paid_on"
                displayMessage="documents:invoices.paid_on"
                closeCallback={closeSetAsPaid}
                saveCallback={
                    paidId
                        ? (params: { date: string }): void => {
                              setAsPaidCallback(paidId, params.date);
                          }
                        : (): void => {}
                }
                firstLoad={firstLoad}
            />
        </>
    );
};

export default withStyles(oplAppStyle)(InvoiceList);
